import React from "react";
import { useTranslation } from "react-i18next";
import Button from "../common/Button";
import useAnalyticsContext from "../../hooks/useAnalyticsContext.js";
import useScrollToHomeSection from "../../hooks/useScrollToHomeSection.js";
import { trackCTA } from "../../lib/analytics";
import useLocalizedHref from "../../hooks/useLocalizedHref.js";

/**
 * "More than a wedding gallery." — the five parts of the platform, as plain
 * text cards. No imagery: these are capability statements, and a decorative
 * photo per card would add weight without adding meaning next to the
 * photography-led arc section directly above.
 *
 * The five cards describe the platform, not this plan. Invitations, RSVPs and
 * the planning tools are Heirloom capabilities (`invitationProduct` in
 * api/constants/planFeatures.mjs), while photo sharing itself is on every plan
 * — so the section carries a qualifier pointing at the plans on the homepage,
 * matching how the gallery and archive-download sections above already qualify
 * themselves. It names no plan and no number: this page deliberately exposes
 * neither.
 *
 * The CTA points at the homepage. Of the dedicated feature pages this section
 * describes, `/wedding-planning` now exists and is linked from the card list
 * it belongs to — the "Planning" card. The rest (`/wedding-website`,
 * `/wedding-rsvp`, `/wedding-photo-qr-code`) are still deliberately NOT linked
 * while they 404.
 */
const CARD_KEYS = [
  "invitations",
  "rsvps",
  "planning",
  "guestExperience",
  "memories",
];

const PhotoSharingExperience = () => {
  const { t } = useTranslation();
  const localize = useLocalizedHref();
  const analyticsContext = useAnalyticsContext();
  const scrollToPlans = useScrollToHomeSection("pricing");

  return (
    <section className="section topless wps-section">
      <div className="container">
        <div className="wps-section-header">
          <h2 className="heading-136">
            {t("weddingPhotoSharing.experience.heading")}
          </h2>
        </div>

        <ul className="wps-feature-list">
          {CARD_KEYS.map((key) => (
            <li key={key} className="wps-feature">
              <h3 className="made-22 wps-feature-title">
                {t(`weddingPhotoSharing.experience.cards.${key}.title`)}
              </h3>
              <p className="body-15 wps-feature-body">
                {t(`weddingPhotoSharing.experience.cards.${key}.body`)}
              </p>
            </li>
          ))}
        </ul>

        <p className="wps-center-action">
          <a
            className="wps-text-link"
            href={localize("/wedding-planning")}
            onClick={() =>
              trackCTA("wedding_photo_sharing_experience", {
                ...analyticsContext,
                ctaName: "wedding_planning",
              })
            }
          >
            {t("weddingPhotoSharing.experience.planningLink")}
          </a>
        </p>

        <p className="wps-plan-note">
          {t("weddingPhotoSharing.experience.note")}{" "}
          <a className="wps-text-link" href={localize("/#pricing")} onClick={scrollToPlans}>
            {t("weddingPhotoSharing.experience.noteLink")}
          </a>
        </p>

        <div className="wps-center-action">
          <Button
            href={localize("/")}
            text={t("weddingPhotoSharing.experience.cta")}
            className="button-lg"
            onClick={() =>
              trackCTA("wedding_photo_sharing_experience", {
                ...analyticsContext,
                ctaName: "explore_agapae",
              })
            }
          />
        </div>
      </div>
    </section>
  );
};

export default PhotoSharingExperience;
